import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import Brand from "@/components/Brand";
import { UserPlus } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    business_name: "",
    email: "",
    password: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await register(form);
      toast.success("Account created");
      navigate("/app/dashboard");
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:block relative border-r border-[var(--border)] grid-lines">
        <div className="h-full flex items-center justify-center p-12">
          <div>
            <Link to="/" className="mb-10 inline-block" data-testid="register-logo">
              <Brand size="lg" />
            </Link>
            <h1 className="font-display text-5xl leading-[0.95] tracking-tight">
              Quote. Invoice.
              <br />
              <span className="text-[var(--primary)]">Crush it.</span>
            </h1>
            <p className="text-zinc-400 mt-6 max-w-sm">
              Free forever for solo tradies. Save clients, quotes and invoices
              in one tidy spot.
            </p>
            <div className="hi-vis-stripe h-1.5 mt-10 w-32" />
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <form
          onSubmit={submit}
          className="w-full max-w-sm card-sharp p-8"
          data-testid="register-form"
        >
          <div className="label-mono">/ REGISTER</div>
          <h2 className="font-display text-3xl mt-2">Create account</h2>
          <div className="mt-6 space-y-4">
            <div>
              <div className="label-mono mb-2">Full name</div>
              <input
                required
                className="input-sharp"
                value={form.name}
                onChange={set("name")}
                data-testid="register-name-input"
              />
            </div>
            <div>
              <div className="label-mono mb-2">Business name (optional)</div>
              <input
                className="input-sharp"
                value={form.business_name}
                onChange={set("business_name")}
                data-testid="register-business-input"
              />
            </div>
            <div>
              <div className="label-mono mb-2">Email</div>
              <input
                required
                type="email"
                className="input-sharp"
                value={form.email}
                onChange={set("email")}
                data-testid="register-email-input"
              />
            </div>
            <div>
              <div className="label-mono mb-2">Password (min 6)</div>
              <input
                required
                type="password"
                className="input-sharp"
                value={form.password}
                onChange={set("password")}
                data-testid="register-password-input"
              />
            </div>
            {error && (
              <div
                className="text-sm font-mono text-[var(--error)] border border-[var(--error)] px-3 py-2"
                data-testid="register-error"
              >
                {error}
              </div>
            )}
            <button
              type="submit"
              className="btn-primary w-full justify-center"
              disabled={submitting}
              data-testid="register-submit-btn"
            >
              <UserPlus size={14} /> {submitting ? "Creating…" : "Create account"}
            </button>
          </div>
          <div className="mt-6 text-center text-xs font-mono text-zinc-500">
            Have an account?{" "}
            <Link to="/login" className="text-[var(--primary)]" data-testid="register-to-login">
              Sign in
            </Link>
          </div>
          <div className="mt-6 pt-6 border-t border-[var(--border)] text-center text-[10px] font-mono text-zinc-500 uppercase tracking-wider leading-relaxed">
            Built by IRD-registered tax agents at NZ Taxation Ltd · Te Atatū, Auckland
          </div>
        </form>
      </div>
    </div>
  );
}
